import { apiAppFetch } from './apiFetch';

function safeGet(getter, fallback = null) {
  try {
    const value = getter();
    return value === undefined ? fallback : value;
  } catch {
    return fallback;
  }
}

function hasWindowKey(pattern) {
  try {
    return Object.getOwnPropertyNames(window).some((key) => pattern.test(key));
  } catch {
    return false;
  }
}

function hasOwn(obj, key) {
  try {
    return obj != null && key in obj && obj[key] != null;
  } catch {
    return false;
  }
}

const WINDOW_AUTOMATION_KEYS = [
  '_Selenium_IDE_Recorder',
  '_selenium',
  'callSelenium',
  'calledSelenium',
  '__webdriver_script_fn',
  '__webdriver_script_func',
  '__webdriver_script_function',
  '__lastWatirAlert',
  '__playwright',
  '__pwInitScripts',
  '__playwright__binding__',
  '__nightmare',
  '_phantom',
  'callPhantom',
  '__phantomas',
  'domAutomation',
  'domAutomationController',
  'sideex',
  'Cypress',
  '__cypress',
  'isAutomaInjected',
  'initPaletteParams',
  '__kantu',
  'Kantu',
  'uiVision',
  '__katalon',
  '__puppeteer_evaluation_script__',
];

const DOCUMENT_AUTOMATION_KEYS = [
  '__webdriver_evaluate',
  '__selenium_evaluate',
  '__fxdriver_evaluate',
  '__driver_evaluate',
  '__webdriver_unwrapped',
  '__selenium_unwrapped',
  '__fxdriver_unwrapped',
  '__driver_unwrapped',
  '__webdriver_script_fn',
  '$cdc_asdjflasutopfhvcZLmcfl_',
  '$chrome_asyncScriptInfo',
  '$wdc_',
];

const AUTOMATION_EXTENSION_IDS = [
  'mooikfkahbdckldjjndioackbalphokd', // Selenium IDE
  'ljdobmomdgdljniojadhoplhkpialdid', // Katalon Recorder
  'infppggnoaenmfagbfknfkancpbljcca', // Automa
  'gcbalfbdmfieckjlnblleoemohcganoc', // Ui.Vision
  'cplklnmnlbnpmjogncfgfijoopmnlemp', // iMacros
  'jggibfiodonlmkjbmlooehabjldddbgn', // Autify Recorder
  'abnnemjpaacaimkkepphpkaiomnafldi', // Qualys Browser Recorder
];

const AUTOMATION_URL_HINTS = [
  /assets\/prompt\.js/i,
  /selenium-ide/i,
  /katalon-recorder/i,
  /katalon/i,
  /sideex/i,
  /(?:^|[\/._-])automa(?:[\/._-]|$)/i,
  /ui\.?vision/i,
  /kantu/i,
  /imacros/i,
  /puppeteer-recorder/i,
];

const AUTOMATION_DOM_MARKERS = [
  '#imacros-highlight-div',
  '#kantu-highlight',
  '#automa-element-selector',
  '.automa-element-selector',
  '[id*="katalon-recorder"]',
  '[class*="katalon-recorder"]',
  '[id*="selenium-ide"]',
  '[data-automa]',
  'automa-element-selector',
];

const extraReasons = [];

function addExtraReason(reason) {
  if (extraReasons.indexOf(reason) === -1) {
    extraReasons.push(reason);
  }
}

function functionLooksLikeRecorderHook(fn) {
  try {
    const src = Function.prototype.toString.call(fn);
    if (!src || src.indexOf('[native code]') !== -1) {
      return false;
    }
    return /from-page-script|SideeXPlayingFlag|recordedPrompt|recordedConfirmation|setPrompt|katalon|sideex|automa|kantu|imacros|selenium/i.test(
      src
    );
  } catch {
    return false;
  }
}

function isAutomationPostMessage(data) {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const type = String(data.type || '');
  if (
    type.indexOf('automa:') === 0 ||
    type.indexOf('katalon') !== -1 ||
    type.indexOf('kantu') !== -1
  ) {
    return true;
  }
  if (data.direction === 'from-content-script' || data.direction === 'from-page-script') {
    return true;
  }
  if (data.recordedType || data.command === 'setNextPromptResult') {
    return true;
  }
  return false;
}

function collectExtensionResourceReasons() {
  const reasons = [];
  if (typeof document === 'undefined') {
    return reasons;
  }

  try {
    const nodes = document.querySelectorAll('script[src], iframe[src], img[src], link[href]');
    for (let i = 0; i < nodes.length; i += 1) {
      const url = nodes[i].src || nodes[i].href || '';
      if (!url) {
        continue;
      }
      for (let j = 0; j < AUTOMATION_EXTENSION_IDS.length; j += 1) {
        if (url.indexOf(AUTOMATION_EXTENSION_IDS[j]) !== -1) {
          reasons.push(`extension_id:${AUTOMATION_EXTENSION_IDS[j]}`);
          return reasons;
        }
      }
      for (let k = 0; k < AUTOMATION_URL_HINTS.length; k += 1) {
        if (AUTOMATION_URL_HINTS[k].test(url)) {
          reasons.push('extension_resource');
          return reasons;
        }
      }
    }
  } catch {
    // ignore
  }

  return reasons;
}

export function getAutomationReasons() {
  const reasons = extraReasons.slice();
  const nav = typeof navigator !== 'undefined' ? navigator : {};

  if (Boolean(nav.webdriver)) {
    reasons.push('webdriver');
  }

  if (
    functionLooksLikeRecorderHook(window.prompt) ||
    functionLooksLikeRecorderHook(window.confirm) ||
    functionLooksLikeRecorderHook(window.alert)
  ) {
    reasons.push('recorder_prompt_hook');
  }

  collectExtensionResourceReasons().forEach((reason) => {
    if (reasons.indexOf(reason) === -1) {
      reasons.push(reason);
    }
  });

  const body = typeof document !== 'undefined' ? document.body : null;
  if (body) {
    ['SideeXPlayingFlag', 'setPrompt', 'setConfirm', 'setAlert'].forEach((attr) => {
      if (body.hasAttribute(attr)) {
        reasons.push(`body:${attr}`);
      }
    });
  }

  const root = typeof document !== 'undefined' ? document.documentElement : null;
  if (root) {
    ['selenium', 'webdriver', 'driver'].forEach((attr) => {
      if (root.getAttribute(attr)) {
        reasons.push(`html:${attr}`);
      }
    });
  }

  WINDOW_AUTOMATION_KEYS.forEach((key) => {
    if (hasOwn(window, key)) {
      reasons.push(`window.${key}`);
    }
  });

  if (typeof document !== 'undefined') {
    DOCUMENT_AUTOMATION_KEYS.forEach((key) => {
      if (hasOwn(document, key)) {
        reasons.push(`document.${key}`);
      }
    });
    AUTOMATION_DOM_MARKERS.forEach((selector) => {
      try {
        if (document.querySelector(selector)) {
          reasons.push(`dom:${selector}`);
        }
      } catch {
        // ignore invalid selector
      }
    });
  }

  if (hasWindowKey(/^(cdc_|\$cdc_)/)) {
    reasons.push('selenium_cdc');
  }

  if (/HeadlessChrome|Headless/i.test(nav.userAgent || '')) {
    reasons.push('headless_ua');
  }

  return reasons;
}

export function isAutomatedBrowser() {
  return getAutomationReasons().length > 0;
}

const AUTOMATION_BLOCK_KEY = 'pt_automation_blocked';

export function isAutomationBlockPersisted() {
  try {
    return (
      sessionStorage.getItem(AUTOMATION_BLOCK_KEY) === '1' ||
      localStorage.getItem(AUTOMATION_BLOCK_KEY) === '1'
    );
  } catch {
    return false;
  }
}

export function persistAutomationBlock() {
  try {
    sessionStorage.setItem(AUTOMATION_BLOCK_KEY, '1');
    localStorage.setItem(AUTOMATION_BLOCK_KEY, '1');
  } catch {
    // ignore quota / private-mode failures
  }
}

export function clearAutomationBlock() {
  try {
    sessionStorage.removeItem(AUTOMATION_BLOCK_KEY);
    localStorage.removeItem(AUTOMATION_BLOCK_KEY);
  } catch {
    // ignore
  }
}

let automationWatchStarted = false;

export function watchForAutomation(onDetected) {
  if (automationWatchStarted || typeof document === 'undefined') {
    return;
  }
  automationWatchStarted = true;

  let fired = false;
  const check = () => {
    if (fired || isAutomationBlockPersisted()) {
      return;
    }
    if (!isAutomatedBrowser()) {
      return;
    }
    fired = true;
    onDetected();
  };

  setInterval(check, 1000);
  document.addEventListener('visibilitychange', check);
  window.addEventListener('message', (event) => {
    if (isAutomationPostMessage(event.data)) {
      addExtraReason('extension_postmessage');
      check();
    }
  });
  [
    '__automa-ext__',
    '__automaExecuteWorkflow',
    'automa:execute-workflow',
    '__automa-fetch__',
  ].forEach((name) => {
    window.addEventListener(name, () => {
      addExtraReason(`event:${name}`);
      check();
    });
  });
  try {
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true,
    });
  } catch {
    // ignore
  }
}

export function collectAutomationDetails() {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const screenObj = typeof window !== 'undefined' ? window.screen : {};
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection || null;
  const userIdRaw = safeGet(() => localStorage.getItem('userId'), '');
  const clientId = userIdRaw && /^\d+$/.test(userIdRaw) ? Number(userIdRaw) : 0;

  const reasons = getAutomationReasons();
  const signals = {
    reasons,
    webdriver: Boolean(nav.webdriver),
    seleniumIdePrompt: reasons.indexOf('recorder_prompt_hook') !== -1,
    documentWebdriverAttr: safeGet(() => document.documentElement.getAttribute('webdriver'), null),
    sideexPlaying: Boolean(document.body && document.body.hasAttribute('SideeXPlayingFlag')),
    userAgentHeadless: /HeadlessChrome|Headless/i.test(nav.userAgent || ''),
    phantom: typeof window.callPhantom !== 'undefined' || typeof window._phantom !== 'undefined',
    nightmare: typeof window.__nightmare !== 'undefined',
    seleniumCDC: hasWindowKey(/^(cdc_|\$cdc_)/),
    playwright:
      typeof window.__playwright !== 'undefined' ||
      typeof window.__pwInitScripts !== 'undefined',
    puppeteer: typeof window.__puppeteer_evaluation_script__ !== 'undefined',
    seleniumIdeRecorder: typeof window._Selenium_IDE_Recorder !== 'undefined',
    selenium: typeof window._selenium !== 'undefined' || typeof window.callSelenium !== 'undefined',
    automa: Boolean(window.isAutomaInjected),
    cypress: typeof window.Cypress !== 'undefined',
    katalon: typeof window.__katalon !== 'undefined',
    kantu: typeof window.__kantu !== 'undefined' || typeof window.Kantu !== 'undefined',
    domAutomation:
      typeof window.domAutomation !== 'undefined' ||
      typeof window.domAutomationController !== 'undefined',
  };

  return {
    clientId,
    pageUrl: safeGet(() => window.location.href, ''),
    hostname: safeGet(() => window.location.hostname, ''),
    pathname: safeGet(() => window.location.pathname, ''),
    search: safeGet(() => window.location.search, ''),
    hash: safeGet(() => window.location.hash, ''),
    referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
    clientTime: new Date().toISOString(),
    timezone: safeGet(() => Intl.DateTimeFormat().resolvedOptions().timeZone, ''),
    timezoneOffset: new Date().getTimezoneOffset(),
    locale: safeGet(() => Intl.DateTimeFormat().resolvedOptions().locale, ''),
    language: nav.language || '',
    languages: Array.from(nav.languages || []),
    userAgent: nav.userAgent || '',
    platform: nav.platform || '',
    vendor: nav.vendor || '',
    product: nav.product || '',
    appName: nav.appName || '',
    appVersion: nav.appVersion || '',
    cookieEnabled: Boolean(nav.cookieEnabled),
    doNotTrack: nav.doNotTrack || '',
    hardwareConcurrency: nav.hardwareConcurrency || 0,
    deviceMemory: nav.deviceMemory || 0,
    maxTouchPoints: nav.maxTouchPoints || 0,
    pdfViewerEnabled: Boolean(nav.pdfViewerEnabled),
    webdriver: Boolean(nav.webdriver),
    pluginsCount: nav.plugins ? nav.plugins.length : 0,
    mimeTypesCount: nav.mimeTypes ? nav.mimeTypes.length : 0,
    screen: {
      width: screenObj.width || 0,
      height: screenObj.height || 0,
      availWidth: screenObj.availWidth || 0,
      availHeight: screenObj.availHeight || 0,
      colorDepth: screenObj.colorDepth || 0,
      pixelDepth: screenObj.pixelDepth || 0,
      orientation: screenObj.orientation ? screenObj.orientation.type : '',
    },
    viewport: {
      innerWidth: window.innerWidth || 0,
      innerHeight: window.innerHeight || 0,
      outerWidth: window.outerWidth || 0,
      outerHeight: window.outerHeight || 0,
      devicePixelRatio: window.devicePixelRatio || 1,
      historyLength: window.history ? window.history.length : 0,
    },
    connection: conn
      ? {
          effectiveType: conn.effectiveType || '',
          downlink: conn.downlink || 0,
          rtt: conn.rtt || 0,
          saveData: Boolean(conn.saveData),
        }
      : null,
    signals,
  };
}

/**
 * Reports detection to the API.
 * enabled: true  → feature on, should block
 * enabled: false → PHP switch off, do not block
 * enabled: null  → request failed; caller should keep an existing block
 */
export async function reportAutomationDetection() {
  try {
    const details = collectAutomationDetails();
    const res = await apiAppFetch('logAutomation', {
      method: 'POST',
      body: JSON.stringify({
        webdriver: Boolean(navigator.webdriver),
        reasons: details.signals && details.signals.reasons ? details.signals.reasons : [],
        clientId: details.clientId || undefined,
        pageUrl: details.pageUrl,
        hostname: details.hostname,
        details,
      }),
    });

    if (!res.ok) {
      return { enabled: null };
    }

    const data = await res.json();
    return { enabled: data.enabled === true };
  } catch {
    return { enabled: null };
  }
}
